import * as React from 'react';
import {Query} from 'react-apollo';
import {Link as RoutedLink} from 'react-router-dom';
// @ts-expect-error
import {components} from 'react-select';
// @ts-expect-error
import type {OptionProps} from 'react-select';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {ErrorMessage} from 'shells/form/error-message';
import {Link} from 'shells/link/link';
import {OptionWithCount} from 'shells/select-option-components/option-with-count';
import type {ListOptionWithCount} from 'shells/select-option-components/option-with-count';

import type {ConstantContactLists as ConstantContactListsQuery} from 'nutshell-graphql-types';

import {GraphQLSelect} from './graphql-select';
import GET_CONSTANT_CONTACT_LISTS from './queries/constant-contact-lists.graphql';

import styles from './graphql-select-constant-contact.module.css';

class ConstantContactQuery extends Query<ConstantContactListsQuery, Record<any, any>> {}

type Props = {
    selectedId: string | null | undefined;
    onChange: (newValue: ListOptionWithCount) => any;
    onClearValue?: () => void;
    labelKey?: string;
    valueKey?: string;
    autoFocus?: boolean;
    autoBlur?: boolean;
    isLoading?: boolean;
    noResultsText?: string;
    styles?: any;
    menuPortalTarget?: HTMLElement;
    defaultsToFirstItem?: boolean;
    menuPlacement?: 'auto' | 'bottom' | 'top';
    onErrorReceived?: (arg1: string) => void;
    shouldRefetch: boolean;
    canAccessIntegrations: boolean;
    isCreateable: boolean;
};

export const GraphQLSelectConstantContact = (props: Props) => (
    <ConstantContactQuery query={GET_CONSTANT_CONTACT_LISTS}>
        {({data, loading, error, refetch}) => {
            const constantContactLists = data ? data.constantContactLists : null;
            // We're not using the error message, but we want to use the logging in the function below
            // @ts-expect-error
            const hasError = Boolean(safelyGetGraphQLErrorMessage(error));

            // map over the list to get the proper value/label options to pass
            // through to the select component below
            const constantContactListOptions = constantContactLists
                ? constantContactLists.map((constantContactList) => ({
                      value: constantContactList.id,
                      label: constantContactList.name,
                      favorite: constantContactList.favorite,
                      count: constantContactList.membershipCount,
                  }))
                : [];

            return (
                <div className={styles.container}>
                    <GraphQLSelect
                        {...props}
                        refetch={refetch}
                        shouldRefetch={props.shouldRefetch}
                        // @ts-expect-error
                        openMenuOnFocus={true}
                        createableEntityText='list'
                        isLoading={loading}
                        placeholder={loading ? 'Finding lists…' : 'Search Constant Contact lists…'}
                        // @ts-expect-error
                        noOptionsMessage={(input) =>
                            input ? 'No matching lists found' : 'No lists found. Try refreshing.'
                        }
                        components={{
                            Option: OptionComponent,
                        }}
                        valueKey='value'
                        labelKey='label'
                        models={constantContactListOptions}
                    />
                    {hasError ? (
                        <ErrorMessage>
                            There was a problem with your connection to Constant Contact, please{' '}
                            {props.canAccessIntegrations ? (
                                <Link
                                    // @ts-expect-error
                                    as={RoutedLink}
                                    variant='primary'
                                    to='/setup/constant-contact'
                                >
                                    re-enable the integration.
                                </Link>
                            ) : (
                                'ask your admin to re-enable the integration'
                            )}
                        </ErrorMessage>
                    ) : (
                        <div className={styles['permission-notice']}>
                            When you add people to Constant Contact, you agree that you have
                            permission to email them.
                        </div>
                    )}
                </div>
            );
        }}
    </ConstantContactQuery>
);

const OptionComponent = (props: OptionProps) => {
    // This is a hack to get around limitations in our old version of flow
    const spreadableProps: OptionProps = props as any;
    const contactDescriptor = props.data.membershipCount === 1 ? 'contact' : 'contacts';
    const title = `${props.data.membershipCount} ${contactDescriptor} in this list`;

    return (
        <components.Option {...spreadableProps}>
            <OptionWithCount
                favorite={props.data.favorite}
                title={title}
                count={props.data.membershipCount}
                label={props.data.label}
            />
        </components.Option>
    );
};
